/*
 * Pre-order enquiry (snippets/cy-preorder-form.liquid).
 * When the selected variant is a pre-order variant, the product page's add to cart (main form and sticky bar) and
 * Buy it now open this dialog instead. The listeners run in the capture phase, so the theme's cart handlers never
 * see those clicks; in-stock variants are untouched. The form posts to the Google Apps Script web app (sheet row,
 * TeleCRM lead, Shopify draft order) and then offers a pre-filled WhatsApp message.
 */
if (!customElements.get('cy-preorder')) {
  const TRIGGERS = '.product-form__submit, .shopify-payment-button__button, shopify-buy-it-now-button, shopify-accelerated-checkout';

  customElements.define(
    'cy-preorder',
    class extends HTMLElement {
      connectedCallback() {
        this.ids = new Set(this.dataset.variants.split(',').filter(Boolean));
        this.variants = JSON.parse(this.querySelector('[data-cy-po-data]').textContent);
        this.dialog = this.querySelector('dialog');
        this.form = this.querySelector('form');
        this.error = this.querySelector('[data-cy-po-error]');
        this.doneTitle = this.querySelector('[data-cy-po-done-title]');
        this.doneText = this.querySelector('[data-cy-po-done-text]');
        this.doneCopy = [this.doneTitle.textContent, this.doneText.textContent];

        this.onIntercept = (e) => this.intercept(e);
        document.addEventListener('click', this.onIntercept, true);
        document.addEventListener('submit', this.onIntercept, true);

        // phones: two steps (contact, then delivery); Enter on step 1 moves on instead of sending
        this.mobile = matchMedia('(max-width: 749px)');
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          if (this.mobile.matches && this.form.dataset.step === '1') this.next();
          else this.send();
        });
        this.querySelector('[data-cy-po-next]').addEventListener('click', () => this.next());
        this.querySelector('[data-cy-po-back]').addEventListener('click', () => this.goTo(1, 'back'));
        // a field's error clears as soon as it is corrected
        this.form.addEventListener('input', (e) => {
          if (e.target.getAttribute('aria-invalid') === 'true' && e.target.checkValidity()) this.mark(e.target, false);
        });
        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-cy-po-close]')) this.dialog.close();
        });
        // a sent enquiry starts fresh next time; an unsent one keeps what was typed
        this.dialog.addEventListener('close', () => {
          if (!this.sent) return;
          this.sent = false;
          this.form.reset();
          this.syncPicker();
        });
        this.setupStates();
        this.setupPincode();
      }

      /* ---------- state picker: the hidden select keeps the value; this list is how it is chosen ---------- */
      setupStates() {
        this.select = this.field('state');
        this.picker = this.querySelector('[data-cy-po-picker]');
        this.panel = this.querySelector('[data-cy-po-states]');
        this.search = this.querySelector('[data-cy-po-state-search]');
        this.list = this.querySelector('[data-cy-po-state-list]');
        this.list.append(
          ...[...this.select.options].filter((o) => o.value).map((o) => {
            const li = document.createElement('li');
            li.id = `CyPoState-${o.value}`;
            li.setAttribute('role', 'option');
            li.dataset.value = o.value;
            li.dataset.key = o.text.toLowerCase();
            li.textContent = o.text;
            return li;
          })
        );
        this.empty = document.createElement('li');
        this.empty.className = 'cy-po__states-empty';
        this.empty.textContent = 'No state matches';
        this.empty.hidden = true;
        this.list.append(this.empty);

        this.picker.addEventListener('click', () => this.openStates());
        this.querySelector('[data-cy-po-states-close]').addEventListener('click', () => this.closeStates());
        this.list.addEventListener('click', (e) => {
          const li = e.target.closest('[role="option"]');
          if (li) this.chooseState(li.dataset.value);
        });
        this.search.addEventListener('input', () => this.filterStates());
        this.search.addEventListener('keydown', (e) => {
          const items = this.visibleStates();
          const at = items.indexOf(this.list.querySelector('.is-active'));
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const next = items[Math.max(0, Math.min(items.length - 1, at + (e.key === 'ArrowDown' ? 1 : -1)))];
            this.activate(next);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (items[Math.max(0, at)]) this.chooseState(items[Math.max(0, at)].dataset.value);
          }
        });
        // Esc closes the list first, not the whole dialog
        this.dialog.addEventListener('cancel', (e) => {
          if (this.panel.hidden) return;
          e.preventDefault();
          this.closeStates();
        });
        this.form.addEventListener('reset', () => setTimeout(() => this.syncPicker()));
      }

      visibleStates() {
        return [...this.list.querySelectorAll('[role="option"]')].filter((li) => !li.hidden);
      }

      activate(li) {
        this.list.querySelectorAll('.is-active').forEach((x) => x.classList.remove('is-active'));
        if (!li) return this.search.removeAttribute('aria-activedescendant');
        li.classList.add('is-active');
        this.search.setAttribute('aria-activedescendant', li.id);
        li.scrollIntoView({ block: 'nearest' });
      }

      filterStates() {
        const q = this.search.value.trim().toLowerCase();
        this.list.querySelectorAll('[role="option"]').forEach((li) => {
          li.hidden = Boolean(q) && !li.dataset.key.includes(q);
        });
        const items = this.visibleStates();
        this.empty.hidden = items.length > 0;
        this.activate(items[0]);
      }

      openStates() {
        this.search.value = '';
        this.filterStates();
        this.list.querySelectorAll('[role="option"]').forEach((li) => {
          li.setAttribute('aria-selected', li.dataset.value === this.select.value ? 'true' : 'false');
        });
        this.activate(this.list.querySelector('[aria-selected="true"]') || this.visibleStates()[0]);
        this.panel.hidden = false;
        this.picker.setAttribute('aria-expanded', 'true');
        this.search.focus({ preventScroll: true });
      }

      closeStates() {
        this.panel.hidden = true;
        this.picker.setAttribute('aria-expanded', 'false');
        this.picker.focus({ preventScroll: true });
      }

      chooseState(value, fromPincode = false) {
        this.select.value = value;
        this.select.dataset.auto = fromPincode ? '1' : '';
        this.syncPicker();
        if (this.select.getAttribute('aria-invalid') === 'true') this.mark(this.select, false);
        if (!fromPincode) this.closeStates();
      }

      syncPicker() {
        const opt = this.select.selectedOptions[0];
        const empty = !this.select.value;
        this.picker.querySelector('[data-cy-po-picker-text]').textContent = opt ? opt.text : '';
        this.picker.toggleAttribute('data-empty', empty);
      }

      /* ---------- pincode: city and state fill in from India Post's public pincode API (only when the
         visitor has not typed them); any failure leaves the fields to be filled by hand ---------- */
      setupPincode() {
        const pin = this.field('pincode');
        const city = this.field('city');
        const hint = this.querySelector('[data-cy-po-pin-hint]');
        const hintText = hint.textContent;
        const cache = new Map();
        const norm = (t) => t.toLowerCase().replace(/&/g, 'and').replace(/[^a-z]/g, '');
        city.addEventListener('input', () => {
          city.dataset.auto = '';
        });
        pin.addEventListener('input', () => {
          const value = pin.value.replace(/\D/g, '');
          hint.classList.remove('is-filled');
          hint.textContent = hintText;
          if (!/^[1-9]\d{5}$/.test(value)) return;
          const fill = (place) => {
            if (!place || pin.value.replace(/\D/g, '') !== value) return;
            if (!city.value.trim() || city.dataset.auto) {
              city.value = place.city;
              city.dataset.auto = '1';
              this.mark(city, false);
            }
            const opt = [...this.select.options].find((o) => o.value && norm(o.text) === norm(place.state));
            if (opt && (!this.select.value || this.select.dataset.auto)) this.chooseState(opt.value, true);
            hint.textContent = `Filled from your pincode: ${place.city}, ${place.state}`;
            hint.classList.add('is-filled');
          };
          if (cache.has(value)) return fill(cache.get(value));
          this.pinRequest?.abort();
          this.pinRequest = new AbortController();
          fetch(`https://api.postalpincode.in/pincode/${value}`, { signal: this.pinRequest.signal })
            .then((res) => res.json())
            .then((data) => {
              const po = data?.[0]?.Status === 'Success' ? data[0].PostOffice?.[0] : null;
              const place = po ? { city: po.District, state: po.State } : null;
              cache.set(value, place);
              if (place) fill(place);
              else hint.textContent = 'We could not find this pincode. Please fill in your city and state.';
            })
            .catch(() => {});
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onIntercept, true);
        document.removeEventListener('submit', this.onIntercept, true);
      }

      field(name) {
        return this.form.elements.namedItem(name);
      }

      intercept(e) {
        const trigger = e.type === 'submit' ? e.target : e.target.closest(TRIGGERS);
        const form = trigger && (trigger.tagName === 'FORM' ? trigger : trigger.closest('form'));
        if (!form || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
        const id = form.querySelector('[name="id"]')?.value;
        if (!this.ids.has(String(id))) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.open(id);
      }

      open(id) {
        const v = this.variants[id] || {};
        this.variantId = id;
        this.field('variant_id').value = id;
        this.field('product_url').value = `${this.dataset.url}?variant=${id}`;
        const img = this.querySelector('[data-cy-po-img]');
        if (v.image) img.src = v.image;
        img.closest('.cy-po__media').hidden = !v.image;
        this.querySelector('[data-cy-po-variant]').textContent = v.title && v.title !== 'Default Title' ? v.title : '';
        this.querySelector('[data-cy-po-price]').textContent = v.price || '';
        // estimated prices (pre-order products above the threshold) are marked and explained
        this.querySelector('[data-cy-po-est]').hidden = !v.estimated;
        this.querySelector('[data-cy-po-est-note]').hidden = !v.estimated;
        this.showStep('form');
        this.error.hidden = true;
        this.form.dataset.step = '1';
        this.form.dataset.dir = '';
        this.updateProgress();
        this.panel.hidden = true;
        this.syncPicker();
        this.dialog.showModal();
        this.field('name').focus();
      }

      group(n) {
        return this.form.querySelector(`[data-cy-po-group="${n}"]`);
      }

      updateProgress() {
        const step = this.form.dataset.step;
        this.querySelector('[data-cy-po-progress]').textContent = `Step ${step} of 2`;
        this.querySelector('[data-cy-po-progress-name]').textContent = this.group(step).querySelector('legend').textContent;
      }

      goTo(n, dir) {
        this.form.dataset.dir = dir;
        this.form.dataset.step = String(n);
        this.updateProgress();
        this.group(n).querySelector('input, textarea, select').focus({ preventScroll: true });
        this.form.closest('.cy-po__sheet').scrollTop = 0;
      }

      // step 1 is checked on its own before moving to delivery
      next() {
        if (this.validate(this.group(1))) this.goTo(2, 'fwd');
      }

      showStep(name) {
        this.querySelectorAll('[data-cy-po-step]').forEach((step) => {
          step.hidden = step.dataset.cyPoStep !== name;
        });
      }

      // shows or clears the message under one field (text comes from the field's data-msg)
      mark(el, bad) {
        el.setAttribute('aria-invalid', bad ? 'true' : 'false');
        if (el === this.select) this.picker.setAttribute('aria-invalid', bad ? 'true' : 'false');
        const msg = el
          .getAttribute('aria-describedby')
          .split(' ')
          .map((id) => this.querySelector(`#${id}`))
          .find((m) => m && m.classList.contains('cy-po__err'));
        if (msg) msg.textContent = bad ? msg.dataset.msg : '';
      }

      // checks the fields inside `scope` (the whole form by default); on phones a problem in step 1 brings it back
      validate(scope = this.form) {
        const phone = this.field('phone');
        phone.value = phone.value.replace(/\D/g, '').replace(/^(91|0)(?=\d{10}$)/, '');
        let first = null;
        scope.querySelectorAll('input, textarea, select').forEach((el) => {
          if (!el.willValidate || el.type === 'hidden' || !el.hasAttribute('aria-describedby')) return;
          if (el.tagName !== 'SELECT') el.value = el.value.trim();
          const bad = !el.checkValidity();
          this.mark(el, bad);
          if (bad && !first) first = el;
        });
        if (first && this.mobile.matches && this.group(1).contains(first) && this.form.dataset.step !== '1') {
          this.goTo(1, 'back');
        }
        if (first) (first === this.select ? this.picker : first).focus();
        return !first;
      }

      // The thank-you screen shows as soon as the form is valid; the enquiry is sent in the background (keepalive,
      // so it still completes if the customer leaves for WhatsApp). The web app takes a few seconds (Shopify, TeleCRM,
      // sheet); if it fails, the same screen switches to asking for WhatsApp so the enquiry is not lost.
      send() {
        this.error.hidden = true;
        if (!this.validate()) return;
        const body = new URLSearchParams(new FormData(this.form));
        this.done();
        fetch(this.dataset.endpoint, { method: 'POST', body, keepalive: true })
          .then((res) => res.json())
          .then((result) => {
            if (!result.ok) this.failed(result.error);
          })
          .catch(() => this.failed());
      }

      // The enquiry was not saved (network error, or the web app refused it): keep what was typed, point to WhatsApp.
      failed(reason) {
        this.sent = false;
        this.doneTitle.textContent = 'Almost there';
        this.doneText.textContent =
          (reason ? reason + ' ' : 'We could not save your request right now. ') +
          'Please send it on WhatsApp so we do not miss it.';
      }

      done() {
        const f = (name) => this.field(name).value;
        const v = this.variants[this.variantId] || {};
        const lines = [
          'Hi Cyphon, I would like to pre-order:',
          this.dataset.product + (v.title && v.title !== 'Default Title' ? ` (${v.title})` : '') + (v.price ? `, ${v.estimated ? 'estimated price ' : ''}${v.price}` : ''),
          f('product_url'),
          '',
          `Name: ${f('name')}`,
          `Mobile: ${f('phone')}`,
          `Email: ${f('email')}`,
          `Address: ${f('address')}, ${f('city')}, ${this.field('state').selectedOptions[0].textContent} - ${f('pincode')}`,
        ];
        this.querySelector('[data-cy-po-wa]').href =
          `https://wa.me/${this.dataset.whatsapp}?text=${encodeURIComponent(lines.join('\n'))}`;
        [this.doneTitle.textContent, this.doneText.textContent] = this.doneCopy;
        this.sent = true;
        this.showStep('done');
        this.doneTitle.focus();
      }
    }
  );
}
